import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Link,
  TextField,
  Button,
  Alert,
  Snackbar,
  useTheme,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Stack,
  alpha,
  Link as MuiLink,
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  Business as BusinessIcon,
  CheckCircle as CheckIcon,
  PointOfSale as PosIcon,
  ArrowBack as ArrowBackIcon,
  Language as WebIcon,
  AccessTime as TimeIcon,
  Headset as HeadsetIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import api from '../../utils/api';

const Contact = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    category: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const contactInfo = [
    {
      icon: <EmailIcon />,
      title: 'Email',
      content: 'support@kaziflex.com',
      link: 'mailto:support@kaziflex.com',
    },
    {
      icon: <WebIcon />,
      title: 'Website',
      content: 'www.kaziflex.com',
      link: 'https://www.kaziflex.com',
    },
    {
      icon: <BusinessIcon />,
      title: 'Company',
      content: 'Kazi Flex LLC',
      link: null,
    },
  ];

  const supportCategories = [
    'General Inquiry',
    'Technical Support',
    'Billing Question',
    'Feature Request',
    'Bug Report',
    'Partnership',
    'Sales Inquiry'
  ];

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setNotification({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      await api.post('/contact', formData);

      setNotification({
        open: true,
        message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon.',
        severity: 'success'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        category: '',
        message: '',
      });
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to send message. Please try again or contact us directly at support@kaziflex.com',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Flex PoS</title>
        <meta name="description" content="Get in touch with the Flex PoS team. We're here to help with questions about our point of sale system." />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Navigation */}
        <Box
          component="nav"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(8px)',
            backgroundColor: alpha(theme.palette.background.paper, 0.95)
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
              {/* Logo */}
              <Box
                component={RouterLink}
                to="/"
                sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                  }}
                >
                  <PosIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Flex PoS
                </Typography>
              </Box>

              {/* Nav Links */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Button component={RouterLink} to="/login" sx={{ fontWeight: 600 }}>
                  Sign In
                </Button>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  sx={{
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                    }
                  }}
                >
                  Get Started
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>

        {/* Hero Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)',
            color: 'white',
            py: { xs: 6, md: 8 },
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
          <Container maxWidth="lg" sx={{ position: 'relative' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{
                color: 'white',
                mb: 3,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Back to Home
            </Button>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 2,
                letterSpacing: '-0.02em'
              }}
            >
              Get in Touch
            </Typography>
            <Typography
              variant="h6"
              sx={{ opacity: 0.9, fontWeight: 400, maxWidth: 600 }}
            >
              Have questions about Flex PoS? We're here to help you succeed.
              Send us a message and we'll respond within 24 hours.
            </Typography>

            {/* Trust Indicators */}
            <Stack direction="row" spacing={4} sx={{ mt: 4 }} flexWrap="wrap">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HeadsetIcon sx={{ opacity: 0.8 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Expert Support</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimeIcon sx={{ opacity: 0.8 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>24hr Response</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon sx={{ opacity: 0.8 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Dedicated Team</Typography>
              </Box>
            </Stack>
          </Container>
        </Box>

        {/* Main Contact Section */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          <Grid container spacing={4}>
            {/* Left Column - Contact Form */}
            <Grid size={{ xs: 12, lg: 7 }}>
              <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Send us a Message
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Fill out the form below and we'll get back to you as soon as possible.
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        required
                        value={formData.name}
                        onChange={handleInputChange('name')}
                        placeholder="John Doe"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        placeholder="john@example.com"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Phone Number (Optional)"
                        value={formData.phone}
                        onChange={handleInputChange('phone')}
                        placeholder="+254 700 000000"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={formData.category}
                          label="Category"
                          onChange={handleInputChange('category')}
                        >
                          {supportCategories.map((category) => (
                            <MenuItem key={category} value={category}>
                              {category}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        label="Subject"
                        value={formData.subject}
                        onChange={handleInputChange('subject')}
                        placeholder="What is this about?"
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        label="Your Message"
                        multiline
                        rows={5}
                        required
                        value={formData.message}
                        onChange={handleInputChange('message')}
                        placeholder="Tell us how we can help you..."
                      />
                    </Grid>
                    <Grid size={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        disabled={loading}
                        sx={{
                          py: 1.5,
                          fontWeight: 600,
                          fontSize: '1rem',
                          background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                          }
                        }}
                      >
                        {loading ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            {/* Right Column - Contact Information */}
            <Grid size={{ xs: 12, lg: 5 }}>
              <Stack spacing={3}>
                {/* Contact Info Card */}
                <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Contact Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Reach out to us directly through any of these channels.
                  </Typography>

                  <Stack spacing={3}>
                    {contactInfo.map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            flexShrink: 0
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {item.title}
                          </Typography>
                          {item.link ? (
                            <Link
                              href={item.link}
                              color="primary"
                              underline="hover"
                              target={item.link.startsWith('http') ? '_blank' : undefined}
                              rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                              sx={{ fontWeight: 600, fontSize: '1rem' }}
                            >
                              {item.content}
                            </Link>
                          ) : (
                            <Typography variant="body1" fontWeight={600}>
                              {item.content}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Paper>

                {/* Response Time Card */}
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    color: 'white',
                    textAlign: 'center'
                  }}
                >
                  <CheckIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Fast Response Guaranteed
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    We typically respond to all inquiries within 24 hours during business days.
                  </Typography>
                </Paper>

              </Stack>
            </Grid>
          </Grid>
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            bgcolor: '#0F172A',
            color: 'white',
            py: 4,
            mt: 'auto'
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5
                  }}
                >
                  <PosIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="body2" sx={{ color: 'grey.500' }}>
                  © {new Date().getFullYear()} Flex PoS. All rights reserved.
                </Typography>
              </Box>
              <Stack direction="row" spacing={3}>
                <MuiLink
                  component={RouterLink}
                  to="/terms"
                  sx={{ color: 'grey.400', textDecoration: 'none', fontSize: '0.875rem', '&:hover': { color: 'white' } }}
                >
                  Terms of Service
                </MuiLink>
                <MuiLink
                  component={RouterLink}
                  to="/terms?tab=privacy"
                  sx={{ color: 'grey.400', textDecoration: 'none', fontSize: '0.875rem', '&:hover': { color: 'white' } }}
                >
                  Privacy Policy
                </MuiLink>
              </Stack>
              <Typography variant="body2" sx={{ color: 'grey.500' }}>
                Developed by{' '}
                <MuiLink
                  href="https://www.kaziflex.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'primary.light', fontWeight: 600, textDecoration: 'none' }}
                >
                  Kazi Flex LLC
                </MuiLink>
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Contact;
